import { api } from './authService';
import { xProjectIdConfig } from '../utils/projectScope';

export interface SyncModelRequest {
  excel_file_path: string;
  options?: {
    backupBeforeSync?: boolean;
    deleteExisting?: boolean;
    validateOnly?: boolean;
  };
}

export interface SyncModelResponse {
  success: boolean;
  message: string;
  data: {
    jobId: string;
    status: string;
    estimatedTime: string;
    progressUrl: string;
  };
}

export interface SyncProgress {
  type: string;
  status: string;
  progress: number;
  message: string;
  currentStep?: string;
  totalSteps?: number;
  currentModel?: string;
  totalModels?: number;
}

// 创建服务对象
export const excelUploadService = {
  /**
   * 上传Excel文件并同步模型
   */
  async uploadAndSync(
    file: File,
    tenantCode: string = 'default',
    modelingType?: 'SQLSERVER' | 'HANA',
    projectId?: string
  ): Promise<any> {
    try {
      console.log('开始上传Excel文件并同步模型');
      
      const formData = new FormData();
      formData.append('excel_file', file);
      formData.append('tenant_code', tenantCode);
      const options: Record<string, unknown> = {
        backup_existing: true,
        create_new_models: true,
        update_existing: true,
        modelingType
      };
      if (projectId !== undefined && projectId !== null && String(projectId).trim() !== '') {
        options.projectId = String(projectId);
      }
      formData.append('options', JSON.stringify(options));

      const projectHeaders = xProjectIdConfig(projectId)?.headers;
      const response = await api.post(
        `/${tenantCode}/sync_model`,
        formData,
        {
          timeout: 60000,
          headers: {
            'Content-Type': 'multipart/form-data',
            ...projectHeaders,
          },
        }
      );

      console.log('上传同步成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('上传同步失败:', error);
      throw new Error(`上传同步失败: ${error.message}`);
    }
  },

  /**
   * 获取同步状态
   */
  async getSyncStatus(tenantCode: string, jobId: string): Promise<any> {
    try {
      console.log(`获取同步状态，租户: ${tenantCode}, 任务ID: ${jobId}`);
      
      // 修复URL，使用正确的路由 - 从 /jobs/ 改为 /status/
      const response = await api.get(
        `/${tenantCode}/sync_model/status/${jobId}`,
        {
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      console.log('获取同步状态成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('获取同步状态失败:', error);
      
      // 提供更详细的错误信息
      if (error.code === 'ECONNABORTED') {
        throw new Error('请求超时，请检查网络连接');
      } else if (error.response) {
        throw new Error(`服务器错误: ${error.response.status} - ${error.response.data?.message || '未知错误'}`);
      } else if (error.request) {
        throw new Error('无法连接到服务器，请检查网络连接');
      } else {
        throw new Error(`获取状态失败: ${error.message}`);
      }
    }
  },

  /**
   * 停止同步任务
   */
  async stopSyncTask(tenantCode: string, jobId: string): Promise<any> {
    try {
      console.log(`停止同步任务，租户: ${tenantCode}, 任务ID: ${jobId}`);
      
      const response = await api.post(
        `/${tenantCode}/sync_model/jobs/${jobId}/stop`,
        {},
        {
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('停止同步任务成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('停止同步任务失败:', error);
      throw new Error(`停止同步任务失败: ${error.message}`);
    }
  },

  /**
   * 停止任务
   */
  async stopJob(tenantCode: string, jobId: string): Promise<any> {
    try {
      console.log(`停止任务，租户: ${tenantCode}, 任务ID: ${jobId}`);
      
      // 修复URL拼接 - 移除重复的 /api
      const response = await api.post(
        `/${tenantCode}/sync_model/jobs/${jobId}/stop`,
        {},
        {
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      console.log('停止任务成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('停止任务失败:', error);
      throw new Error(`停止任务失败: ${error.message}`);
    }
  },

  /**
   * 上传Excel文件并同步指令
   * @param projectId 目标 project_id，写入 options 并附加请求头 X-Project-Id
   */
  async uploadAndSyncInstructions(
    file: File,
    tenantCode: string = 'default',
    targetServerConfig?: any,
    projectId?: string
  ): Promise<any> {
    try {
      console.log('开始上传Excel文件并同步指令');
      
      const formData = new FormData();
      formData.append('excel_file', file);
      formData.append('tenant_code', tenantCode);
      if (targetServerConfig) {
        formData.append('target_server_config', JSON.stringify(targetServerConfig));
      }
      const optionsPayload: Record<string, unknown> = {};
      if (projectId !== undefined && projectId !== null && String(projectId).trim() !== '') {
        optionsPayload.projectId = String(projectId);
      }
      if (Object.keys(optionsPayload).length > 0) {
        formData.append('options', JSON.stringify(optionsPayload));
      }

      const projectHeaders = xProjectIdConfig(projectId)?.headers;
      const response = await api.post(
        `/${tenantCode}/sync_instruction/upload`,
        formData,
        {
          timeout: 60000,
          headers: {
            'Content-Type': 'multipart/form-data',
            ...projectHeaders,
          },
        }
      );

      console.log('上传同步指令成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('上传同步指令失败:', error);
      throw new Error(`上传同步指令失败: ${error.message}`);
    }
  },

  /**
   * 上传 Excel 并同步 SQL Pair 到 ChatBI
   */
  async uploadAndSyncSqlPairs(
    file: File,
    tenantCode: string = 'default',
    targetServerConfig?: any,
    projectId?: string
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('excel_file', file);
      formData.append('tenant_code', tenantCode);
      if (targetServerConfig) {
        formData.append('target_server_config', JSON.stringify(targetServerConfig));
      }
      const optionsPayload: Record<string, unknown> = {};
      if (projectId !== undefined && projectId !== null && String(projectId).trim() !== '') {
        optionsPayload.projectId = String(projectId);
      }
      if (Object.keys(optionsPayload).length > 0) {
        formData.append('options', JSON.stringify(optionsPayload));
      }

      const projectHeaders = xProjectIdConfig(projectId)?.headers;
      const response = await api.post(`/${tenantCode}/sync_sql_pair/upload`, formData, {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...projectHeaders,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('上传同步 SQL Pair 失败:', error);
      throw new Error(`上传同步 SQL Pair 失败: ${error.message}`);
    }
  },
};

// 添加默认导出
export default excelUploadService;
